import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import './index.css';
import api from './utils/api';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import NewQuote from './pages/NewQuote';
import Quotes from './pages/Quotes';
import Clients from './pages/Clients';
import Rates from './pages/Rates';
import Users from './pages/Users';
import Settings from './pages/Settings';

export const AppContext = createContext(null);
export function useApp() { return useContext(AppContext); }

const NAV = [
  { id:'dashboard', label:'Dashboard',     icon:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg> },
  { id:'newquote',  label:'New quote',      icon:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9"/><path d="M12 8v8M8 12h8"/></svg> },
  { id:'quotes',    label:'Quotes',         icon:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="13" y2="17"/></svg> },
  { id:'clients',   label:'Clients',        icon:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
];
const ADMIN_NAV = [
  { id:'rates',    label:'Rates & pricing', icon:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg> },
  { id:'users',    label:'Users',           icon:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> },
  { id:'settings', label:'Settings',        icon:<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg> },
];

const PAGES = { dashboard:Dashboard, newquote:NewQuote, quotes:Quotes, clients:Clients, rates:Rates, users:Users, settings:Settings };

export default function App() {
  const [user, setUser]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState('dashboard');
  const [toasts, setToasts]     = useState([]);
  const [clients, setClients]   = useState([]);
  const [rates, setRates]       = useState([]);
  const [settings, setSettings] = useState({});

  const toast = useCallback((msg, type='') => {
    const id = Date.now();
    setToasts(t => [...t, { id, msg, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3400);
  }, []);

  const loadGlobals = useCallback(async () => {
    try {
      const [c, r, s] = await Promise.all([api.get('/clients'), api.get('/rates'), api.get('/settings')]);
      setClients(c); setRates(r); setSettings(s);
    } catch(e) { console.error(e); }
  }, []);

  useEffect(() => {
    const token = api.getToken();
    if (!token) { setLoading(false); return; }
    api.get('/auth/me')
      .then(u => { setUser(u); loadGlobals(); })
      .catch(() => api.setToken(null))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const data = await api.post('/auth/login', { email, password });
    api.setToken(data.token);
    setUser(data.user);
    await loadGlobals();
    setPage('dashboard');
  };

  const logout = () => { api.setToken(null); setUser(null); setPage('dashboard'); };

  if (loading) return (
    <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'var(--navy)',flexDirection:'column',gap:20 }}>
      <img src="/pas_logo.png" alt="PAS" style={{ height:52,filter:'brightness(0) invert(1)',opacity:0.8 }} onError={e=>e.target.style.display='none'}/>
      <div style={{ display:'flex',gap:6 }}>
        {[0,1,2].map(i => (
          <div key={i} style={{ width:6,height:6,borderRadius:'50%',background:'rgba(255,255,255,0.3)',animation:`pulse-ring 1.2s ease-in-out ${i*0.2}s infinite` }}/>
        ))}
      </div>
    </div>
  );

  if (!user) return <Login onLogin={login} />;

  const nav = [...NAV, ...(user.role==='admin' ? ADMIN_NAV : [])];
  const initials = user.name.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
  const PageComponent = PAGES[page] || Dashboard;
  const ctx = { user, clients, rates, settings, toast, loadGlobals, setClients, setRates, setSettings, setPage };

  return (
    <AppContext.Provider value={ctx}>
      <div className="app-shell">
        <aside className="sidebar animate-slide-right">
          <div className="sidebar-logo-area">
            <img src="/pas_logo.png" alt="PAS Trinidad" onError={e=>{e.target.style.display='none';e.target.nextSibling.style.display='block';}}/>
            <div style={{ display:'none', fontFamily:'var(--font-display)', fontSize:16, color:'#fff', fontWeight:700 }}>PAS Trinidad</div>
            <div className="sidebar-tagline">Quotation System</div>
          </div>

          <div className="sidebar-divider"/>

          <div className="sidebar-section">Navigation</div>
          {nav.map(n => (
            <button key={n.id} className={`nav-item ${page===n.id?'active':''}`} onClick={()=>setPage(n.id)}>
              {n.icon} {n.label}
            </button>
          ))}

          {user.role === 'admin' && (
            <div style={{ margin:'8px 12px', padding:'8px 12px', background:'rgba(196,98,26,0.08)', borderRadius:'var(--r-sm)', border:'1px solid rgba(196,98,26,0.15)' }}>
              <div style={{ fontFamily:'var(--font-mono)', fontSize:9, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--copper)', marginBottom:2 }}>Admin access</div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.35)' }}>Full system control</div>
            </div>
          )}

          <div className="sidebar-user">
            <div className="sidebar-user-card">
              <div className="sidebar-avatar">{initials}</div>
              <div>
                <div className="sidebar-user-name">{user.name}</div>
                <div className="sidebar-user-role">{user.role === 'admin' ? '⚡ Admin' : '● Sales'}</div>
              </div>
            </div>
            <button className="sidebar-logout" onClick={logout}>Sign out</button>
          </div>
        </aside>

        <main className="main">
          <div className="page">
            <PageComponent key={page}/>
          </div>
        </main>
      </div>

      <div className="toast-container">
        {toasts.map(t => {
          const icon = t.type==='success'?'✓':t.type==='error'?'✕':'→';
          return (
            <div key={t.id} className={`toast toast-${t.type||'default'}`}>
              <div className="toast-icon">{icon}</div>
              {t.msg}
            </div>
          );
        })}
      </div>
    </AppContext.Provider>
  );
}
